/* ==========================================================================
   Maison de Marie — shared motion controller
   --------------------------------------------------------------------------
   Loaded once per page by snippets/mdm-core.liquid. Every mdm-* section opts
   in purely through data attributes; there is no per-section script.

   Responsibilities
     · splits headings into visual lines + words (RTL safe, font-aware)
     · reveals each scope when it scrolls into view, honouring per-element
       delay / stagger
     · damped scroll parallax (single rAF loop, lerped — never raw scrollTop)
     · magnetic + spotlight pointer effects on fine pointers only
     · relayouts split text on resize without replaying the animation
     · re-initialises for the Shopify theme editor
   ========================================================================== */

(function () {
  'use strict';

  if (window.MdmMotion) return;

  var MdmMotionDebug = {};

  var docEl = document.documentElement;
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Elements we are tracking, keyed by concern. */
  var scopes = [];
  var parallaxItems = [];
  var magnetItems = [];
  var splitItems = [];
  var marqueeItems = [];

  var observer = null;
  var rafId = 0;
  var loopActive = false;
  var lastWidth = window.innerWidth;
  var resizeTimer = 0;

  var decoder = document.createElement('textarea');

  /* ---------------------------------------------------------------- utils */

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function decodeEntities(html) {
    decoder.innerHTML = html;
    return decoder.value;
  }

  function lerp(from, to, amount) {
    return from + (to - from) * amount;
  }

  function reduced() {
    return reduceQuery.matches;
  }

  /* Re-enable transitions after forcing an element to a final state. */
  function withoutTransition(el, fn) {
    el.classList.add('mdm-instant');
    fn();
    // Force a reflow so the class removal cannot be batched with the change.
    void el.offsetWidth;
    el.classList.remove('mdm-instant');
  }

  /* ------------------------------------------------------------ splitting */

  /* Cuts a heading into visual lines, each line a mask, each word its own
     animated inline-block. Words are never split into characters: that would
     break Arabic cursive joining. */
  function splitElement(el) {
    var original = el.__mdmOriginal;

    if (original == null) {
      // Refuse anything with real markup inside — only text and <br>.
      var safe = true;
      toArray(el.childNodes).forEach(function (node) {
        if (node.nodeType === 1 && node.tagName !== 'BR') safe = false;
      });
      if (!safe) return false;
      original = el.__mdmOriginal = el.innerHTML;
    }

    var mode = el.getAttribute('data-mdm-split') === 'lines' ? 'lines' : 'words';
    var segments = original.split(/<br\s*\/?>/i);

    // Pass 1 — lay the words out normally so the browser can tell us where
    // the line breaks actually fall for this locale, font and container.
    el.textContent = '';
    var words = [];

    segments.forEach(function (segment, index) {
      var text = decodeEntities(segment).trim();
      if (text) {
        text.split(/\s+/).forEach(function (word, wordIndex) {
          if (wordIndex > 0) el.appendChild(document.createTextNode(' '));
          var span = document.createElement('span');
          span.className = 'mdm-word';
          span.textContent = word;
          el.appendChild(span);
          words.push(span);
        });
      }
      if (index < segments.length - 1) el.appendChild(document.createElement('br'));
    });

    if (!words.length) {
      el.innerHTML = original;
      return false;
    }

    // Pass 2 — group by vertical position, then rebuild inside line masks.
    var lines = [];
    var currentTop = null;
    words.forEach(function (word) {
      var top = Math.round(word.offsetTop);
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        currentTop = top;
        lines.push([]);
      }
      lines[lines.length - 1].push(word);
    });

    el.textContent = '';
    var counter = 0;

    lines.forEach(function (lineWords) {
      var line = document.createElement('span');
      line.className = 'mdm-line';

      lineWords.forEach(function (word, index) {
        if (index > 0) line.appendChild(document.createTextNode(' '));
        if (mode === 'words') {
          word.setAttribute('data-mdm-animate', 'word');
          word.style.setProperty('--mdm-i', counter + index);
        }
        line.appendChild(word);
      });

      if (mode === 'lines') {
        var inner = document.createElement('span');
        inner.setAttribute('data-mdm-animate', 'line');
        inner.style.setProperty('--mdm-i', counter);
        while (line.firstChild) inner.appendChild(line.firstChild);
        line.appendChild(inner);
      }

      counter += mode === 'lines' ? 1 : lineWords.length;
      el.appendChild(line);
    });

    el.__mdmSplit = true;
    return true;
  }

  /* --------------------------------------------------------------- scopes */

  function collectAnimated(scopeEl) {
    return toArray(scopeEl.querySelectorAll('[data-mdm-animate]')).filter(function (el) {
      return el.closest('[data-mdm-scope]') === scopeEl;
    });
  }

  /* Containers marked data-mdm-stagger hand an increasing index to their
     direct animated children. Grandchildren inherit that index through the
     custom property, so an icon inside a staggered row lands with its row. */
  function applyStagger(scopeEl) {
    toArray(scopeEl.querySelectorAll('[data-mdm-stagger]')).forEach(function (container) {
      var step = parseFloat(container.getAttribute('data-mdm-stagger'));
      if (!isNaN(step)) container.style.setProperty('--mdm-step', step + 'ms');

      var index = 0;
      toArray(container.children).forEach(function (child) {
        if (!child.hasAttribute('data-mdm-animate')) return;
        if (child.style.getPropertyValue('--mdm-i') === '') {
          child.style.setProperty('--mdm-i', index);
        }
        index += 1;
      });
    });
  }

  function reveal(scope) {
    if (scope.revealed) return;
    scope.revealed = true;
    scope.el.classList.add('mdm-in');
    scope.items.forEach(function (el) {
      el.classList.add('mdm-in');
    });
  }

  function registerScope(scopeEl) {
    if (scopeEl.__mdmScope) return scopeEl.__mdmScope;

    applyStagger(scopeEl);

    var scope = {
      el: scopeEl,
      items: collectAnimated(scopeEl),
      revealed: false
    };

    scopeEl.__mdmScope = scope;
    scopes.push(scope);

    if (reduced()) {
      reveal(scope);
      return scope;
    }

    observer.observe(scopeEl);

    /* Failsafe: if the observer never fires (an odd embed, a hidden parent
       that later shows), the content still appears. */
    window.setTimeout(function () {
      reveal(scope);
    }, 2600);

    return scope;
  }

  /* Rebuild split text after a width change, keeping the current state. */
  function relayoutSplits() {
    splitItems.forEach(function (el) {
      var scopeEl = el.closest('[data-mdm-scope]');
      var scope = scopeEl && scopeEl.__mdmScope;
      var wasRevealed = scope ? scope.revealed : false;

      splitElement(el);

      if (scope) {
        scope.items = collectAnimated(scopeEl);
        if (wasRevealed) {
          withoutTransition(el, function () {
            scope.items.forEach(function (item) {
              item.classList.add('mdm-in');
            });
          });
        }
      }
    });
  }

  /* -------------------------------------------------------------- marquee */

  /* Measures one group, clones it until the strip covers the frame plus one
     spare group, then derives the duration from the measured width so the
     travel speed is constant whatever the content is. Safe to re-run: it
     clears its own clones first. */
  function setupMarquee(el, attempt) {
    var track = el.querySelector('[data-mdm-marquee-track]');
    if (!track) return;

    var group = track.querySelector('[data-mdm-marquee-group]');
    if (!group) return;

    el.classList.remove('mdm-marquee-ready');
    toArray(track.querySelectorAll('[data-mdm-marquee-clone]')).forEach(function (clone) {
      track.removeChild(clone);
    });

    var groupWidth = group.getBoundingClientRect().width;
    var frameWidth = el.getBoundingClientRect().width;

    /* Nothing to measure yet — a section the theme editor has just injected, a
       collapsed parent, a font still swapping. Keep trying for a few frames
       instead of giving up for good, which would leave a dead strip. */
    if (!groupWidth || !frameWidth) {
      var tries = attempt || 0;
      if (tries < 30) {
        window.requestAnimationFrame(function () {
          setupMarquee(el, tries + 1);
        });
      }
      return;
    }

    var speed = parseFloat(el.getAttribute('data-mdm-marquee'));
    if (isNaN(speed) || speed <= 0) speed = 60;

    // One spare group beyond the frame guarantees no gap at any point of the
    // cycle, since the strip only ever travels one group width.
    var copies = Math.max(2, Math.ceil(frameWidth / groupWidth) + 1);

    for (var i = 1; i < copies; i++) {
      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-mdm-marquee-clone', '');
      track.appendChild(clone);
    }

    el.style.setProperty('--mdm-marquee-shift', (-100 / copies).toFixed(4) + '%');
    el.style.setProperty('--mdm-marquee-duration', (groupWidth / speed).toFixed(2) + 's');
    el.classList.add('mdm-marquee-ready');
  }

  /* Re-measures whenever the frame changes size. This also rescues a marquee
     whose first measurement came back as zero, because the observer fires as
     soon as the element is given a box. */
  function observeMarqueeSize(el) {
    if (el.__mdmMarqueeRO || !window.ResizeObserver) return;

    var timer = 0;
    var ro = new ResizeObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        setupMarquee(el);
      }, 150);
    });

    // Only the frame is observed. Cloning changes the track's width, not the
    // frame's, so this cannot feed back into itself.
    ro.observe(el);
    el.__mdmMarqueeRO = ro;
  }

  function registerMarquees(scopeEl) {
    toArray(scopeEl.querySelectorAll('[data-mdm-marquee]')).forEach(function (el) {
      if (marqueeItems.indexOf(el) === -1) marqueeItems.push(el);
      if (!el.classList.contains('mdm-marquee-ready')) setupMarquee(el);
      observeMarqueeSize(el);
    });
  }

  /* ------------------------------------------------------- parallax / rAF */

  function registerParallax(scopeEl) {
    toArray(scopeEl.querySelectorAll('[data-mdm-parallax]')).forEach(function (el) {
      if (el.__mdmParallax) return;
      var speed = parseFloat(el.getAttribute('data-mdm-parallax'));
      el.__mdmParallax = {
        el: el,
        speed: isNaN(speed) ? 0.12 : speed,
        current: 0,
        target: 0,
        visible: false
      };
      parallaxItems.push(el.__mdmParallax);
      visibilityObserver.observe(el);
    });
  }

  function registerMagnets(scopeEl) {
    toArray(scopeEl.querySelectorAll('[data-mdm-magnetic], [data-mdm-spotlight]')).forEach(
      function (el) {
        if (el.__mdmMagnet) return;

        var strength = parseFloat(el.getAttribute('data-mdm-magnetic'));
        var item = {
          el: el,
          strength: isNaN(strength) ? 0 : strength,
          spotlight: el.hasAttribute('data-mdm-spotlight'),
          current: { x: 0, y: 0 },
          target: { x: 0, y: 0 },
          active: false
        };

        el.__mdmMagnet = item;
        magnetItems.push(item);

        el.addEventListener('pointermove', function (event) {
          if (!finePointer.matches || reduced()) return;
          var rect = el.getBoundingClientRect();
          var relX = event.clientX - rect.left;
          var relY = event.clientY - rect.top;

          if (item.spotlight) {
            el.style.setProperty('--mdm-mx', relX.toFixed(1) + 'px');
            el.style.setProperty('--mdm-my', relY.toFixed(1) + 'px');
          }

          if (item.strength) {
            item.target.x = ((relX - rect.width / 2) / (rect.width / 2)) * item.strength;
            item.target.y = ((relY - rect.height / 2) / (rect.height / 2)) * item.strength;
            item.active = true;
            startLoop();
          }
        });

        el.addEventListener('pointerleave', function () {
          item.target.x = 0;
          item.target.y = 0;
          item.active = true;
          if (item.spotlight) {
            el.style.setProperty('--mdm-mx', '50%');
            el.style.setProperty('--mdm-my', '50%');
          }
          startLoop();
        });
      }
    );
  }

  /* One shared loop for parallax and magnets. It only spins while something
     is actually in view and moving, and it sleeps with the tab. */
  function tick() {
    rafId = 0;
    var busy = false;
    var viewportCentre = window.innerHeight / 2;

    for (var i = 0; i < parallaxItems.length; i++) {
      var item = parallaxItems[i];
      if (!item.visible) continue;

      var rect = item.el.getBoundingClientRect();
      var centre = rect.top + rect.height / 2;
      item.target = (viewportCentre - centre) * item.speed;
      item.current = lerp(item.current, item.target, 0.085);

      if (Math.abs(item.target - item.current) > 0.05) busy = true;
      item.el.style.setProperty('--mdm-parallax', item.current.toFixed(2) + 'px');
    }

    for (var j = 0; j < magnetItems.length; j++) {
      var magnet = magnetItems[j];
      if (!magnet.active) continue;

      magnet.current.x = lerp(magnet.current.x, magnet.target.x, 0.14);
      magnet.current.y = lerp(magnet.current.y, magnet.target.y, 0.14);

      var settled =
        Math.abs(magnet.target.x - magnet.current.x) < 0.05 &&
        Math.abs(magnet.target.y - magnet.current.y) < 0.05;

      if (settled) {
        magnet.current.x = magnet.target.x;
        magnet.current.y = magnet.target.y;
        magnet.active = magnet.target.x !== 0 || magnet.target.y !== 0;
      } else {
        busy = true;
      }

      magnet.el.style.setProperty('--mdm-mag-x', magnet.current.x.toFixed(2) + 'px');
      magnet.el.style.setProperty('--mdm-mag-y', magnet.current.y.toFixed(2) + 'px');
    }

    if (busy && loopActive) rafId = window.requestAnimationFrame(tick);
    else loopActive = false;
  }

  function startLoop() {
    if (loopActive || reduced() || document.hidden) return;
    loopActive = true;
    rafId = window.requestAnimationFrame(tick);
  }

  function stopLoop() {
    loopActive = false;
    if (rafId) window.cancelAnimationFrame(rafId);
    rafId = 0;
  }

  /* Tracks which parallax plates are on screen, so the loop can idle. */
  var visibilityObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var item = entry.target.__mdmParallax;
        if (!item) return;
        item.visible = entry.isIntersecting;
        if (entry.isIntersecting) startLoop();
      });
    },
    { rootMargin: '15% 0px 15% 0px' }
  );

  /* ------------------------------------------------------------ accordion */

  /* Animates native <details>. The markup stays <details>/<summary>, so it
     opens and closes perfectly well with no JavaScript at all — this only
     adds the height transition, which <details> cannot do on its own
     because it hides its content outright when closed.

       <div data-mdm-accordion="single">
         <details data-mdm-acc>
           <summary> … </summary>
           <div data-mdm-acc-panel> … </div>
         </details>
       </div>
  */
  function setupAccordion(root) {
    if (root.__mdmAccordion) return;
    root.__mdmAccordion = true;

    var single = root.getAttribute('data-mdm-accordion') === 'single';
    var items = toArray(root.querySelectorAll('[data-mdm-acc]'));

    items.forEach(function (item) {
      var summary = item.querySelector('summary');
      var panel = item.querySelector('[data-mdm-acc-panel]');
      if (!summary || !panel) return;

      var running = null;
      var guard = 0;

      function settle(open) {
        window.clearTimeout(guard);
        if (running) {
          running.onfinish = null;
          running.cancel();
        }
        running = null;
        panel.style.height = '';
        panel.style.overflow = '';
        item.open = open;
      }

      function slide(from, to, open) {
        var duration = 420;

        /* A hidden tab does not tick Web Animations, so onfinish would never
           arrive and the open state would never settle. Skip the animation
           outright, and back the callback with a timer in every other case. */
        if (reduced() || document.hidden || !panel.animate) {
          settle(open);
          return;
        }

        if (running) {
          running.onfinish = null;
          running.cancel();
        }

        panel.style.overflow = 'hidden';
        running = panel.animate(
          [{ height: from + 'px' }, { height: to + 'px' }],
          { duration: duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );

        running.onfinish = function () {
          settle(open);
        };

        window.clearTimeout(guard);
        guard = window.setTimeout(function () {
          settle(open);
        }, duration + 120);
      }

      function expand() {
        // <details> only lays its panel out once it is open, so open first
        // and animate from zero.
        item.open = true;
        slide(0, panel.scrollHeight, true);
      }

      function collapse() {
        // Stay open for the duration, then close on finish.
        slide(panel.getBoundingClientRect().height, 0, false);
      }

      item.__mdmCollapse = collapse;

      summary.addEventListener('click', function (event) {
        event.preventDefault();

        if (item.open) {
          collapse();
          return;
        }

        if (single) {
          items.forEach(function (other) {
            if (other !== item && other.open && other.__mdmCollapse) other.__mdmCollapse();
          });
        }

        expand();
      });
    });
  }

  /* ------------------------------------------------------------- carousel */

  /* Multi-per-view slide carousel with dots, drag and an "active" slide.

     Unlike the announcement slider this steps by one card while showing
     several, so the index is clamped to the last full page rather than
     wrapping. The centred card gets .is-active, which is what lets a design
     highlight the middle of the row.

       <div data-mdm-carousel data-mdm-carousel-autoplay="0">
         <div data-mdm-carousel-viewport>
           <div data-mdm-carousel-track>
             <article data-mdm-carousel-slide> … </article>
           </div>
         </div>
         <div data-mdm-carousel-dots></div>
       </div>
  */
  function setupCarousel(el) {
    if (el.__mdmCarousel) return;
    el.__mdmCarousel = true;

    var viewport = el.querySelector('[data-mdm-carousel-viewport]');
    var track = el.querySelector('[data-mdm-carousel-track]');
    var slides = toArray(el.querySelectorAll('[data-mdm-carousel-slide]'));
    var dotsBox = el.querySelector('[data-mdm-carousel-dots]');
    var prev = el.querySelector('[data-mdm-carousel-prev]');
    var next = el.querySelector('[data-mdm-carousel-next]');

    if (!viewport || !track || !slides.length) return;

    var dotLabel = el.getAttribute('data-mdm-carousel-dot-label') || 'Go to slide';
    var autoplay = parseFloat(el.getAttribute('data-mdm-carousel-autoplay'));
    if (isNaN(autoplay) || autoplay < 1200) autoplay = 0;

    var index = 0;
    var step = 0;
    var perView = 1;
    var maxIndex = 0;
    var timer = 0;
    var dragging = false;
    var pressed = false;
    var startX = 0;
    var startY = 0;
    var delta = 0;
    var dots = [];
    var native = false;

    function dirSign() {
      return window.getComputedStyle(el).direction === 'rtl' ? -1 : 1;
    }

    function baseX() {
      return -index * step * dirSign();
    }

    function place(x, animate) {
      track.style.transition = animate ? '' : 'none';
      track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    /* The card in the middle of what is currently visible. */
    function markActive() {
      var active = Math.min(slides.length - 1, index + Math.floor(perView / 2));
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === active);
      });
    }

    function markDots() {
      dots.forEach(function (dot, i) {
        var on = i === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= maxIndex;
    }

    function go(to, animate) {
      index = Math.max(0, Math.min(maxIndex, to));

      if (native) {
        /* Touch devices scroll the row themselves, so paging is a scroll of
           the row — never scrollIntoView, which also scrolls the PAGE to
           reveal the row and would throw the reader down to this section on
           load and on every resize. The offset is measured against the
           viewport, so it holds in both reading directions. */
        var target = slides[index];
        if (target) {
          var vpRect = viewport.getBoundingClientRect();
          var tRect = target.getBoundingClientRect();
          var delta = dirSign() === -1 ? tRect.right - vpRect.right : tRect.left - vpRect.left;
          var left = viewport.scrollLeft + delta;

          if (viewport.scrollTo) {
            viewport.scrollTo({
              left: left,
              behavior: animate === false || reduced() || document.hidden ? 'auto' : 'smooth'
            });
          } else {
            viewport.scrollLeft = left;
          }
        }
      } else {
        place(baseX(), animate !== false);
      }

      markActive();
      markDots();
    }

    function buildDots() {
      if (!dotsBox) return;
      var count = maxIndex + 1;
      if (dots.length === count) return;

      dotsBox.textContent = '';
      dots = [];

      /* A single page needs no pagination at all. */
      dotsBox.hidden = count < 2;
      if (count < 2) return;

      for (var i = 0; i < count; i++) {
        (function (target) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'mdm-dot';
          dot.setAttribute('aria-label', dotLabel + ' ' + (target + 1));
          dot.addEventListener('click', function () {
            stop();
            go(target);
            play();
          });
          dotsBox.appendChild(dot);
          dots.push(dot);
        })(i);
      }
    }

    /* The stylesheet decides: where the viewport scrolls itself (touch
       devices), the platform owns the gesture and the script only follows
       along. Dragging a transform is for pointers that can hover. */
    function isNative() {
      var ox = window.getComputedStyle(viewport).overflowX;
      return ox === 'auto' || ox === 'scroll';
    }

    function measure() {
      var first = slides[0].getBoundingClientRect().width;
      if (!first) return;

      native = isNative();
      if (native) {
        track.style.transition = '';
        track.style.transform = '';
      }

      var gap = parseFloat(window.getComputedStyle(track).columnGap);
      if (isNaN(gap)) gap = 0;

      step = first + gap;
      perView = Math.max(1, Math.round((viewport.getBoundingClientRect().width + gap) / step));
      maxIndex = Math.max(0, slides.length - perView);

      /* Nothing to page through means no controls at all, rather than two
         permanently disabled arrows sitting under the row. */
      if (prev) prev.hidden = maxIndex < 1;
      if (next) next.hidden = maxIndex < 1;

      buildDots();
      go(Math.min(index, maxIndex), false);
    }

    /* When the platform scrolls, position is the source of truth: read it
       back so the dots and the highlighted card follow a swipe. */
    var scrollTimer = 0;
    viewport.addEventListener(
      'scroll',
      function () {
        if (!native || !step) return;
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          var at = Math.round(Math.abs(viewport.scrollLeft) / step);
          index = Math.max(0, Math.min(maxIndex, at));
          markActive();
          markDots();
        }, 90);
      },
      { passive: true }
    );

    function stop() {
      window.clearInterval(timer);
      timer = 0;
    }

    function play() {
      stop();
      if (!autoplay || reduced() || document.hidden || maxIndex < 1) return;
      timer = window.setInterval(function () {
        go(index >= maxIndex ? 0 : index + 1);
      }, autoplay);
    }

    /* ---- drag and swipe ----
       One path covers mouse, pen and touch. The gesture is only claimed
       once it is clearly sideways: until then a touch can still scroll the
       page, and a tap is still a tap. The element also needs
       touch-action: pan-y in CSS, or the browser keeps horizontal touch
       gestures for itself and cancels the pointer before any of this runs. */
    el.addEventListener('pointerdown', function (event) {
      if (native) return;
      if (event.button || maxIndex < 1) return;
      if (event.target.closest('a, button')) return;
      pressed = true;
      dragging = false;
      startX = event.clientX;
      startY = event.clientY;
      delta = 0;
      stop();
    });

    el.addEventListener('pointermove', function (event) {
      if (native || !pressed) return;

      var dx = event.clientX - startX;
      var dy = event.clientY - startY;

      if (!dragging) {
        // Undecided: wait for a clear direction.
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          // A vertical gesture belongs to the page.
          pressed = false;
          play();
          return;
        }
        dragging = true;
        if (el.setPointerCapture) {
          try {
            el.setPointerCapture(event.pointerId);
          } catch (e) {}
        }
      }

      delta = dx;
      place(baseX() + delta, false);
    });

    function endDrag() {
      if (!dragging) {
        // A tap or a vertical scroll: nothing to settle, but autoplay was
        // paused on press and has to come back.
        if (pressed) {
          pressed = false;
          play();
        }
        return;
      }
      pressed = false;
      dragging = false;

      var threshold = Math.max(30, step * 0.22);
      // delta * dirSign is negative when the gesture means "next" in either
      // reading direction.
      var moved = delta * dirSign();

      if (moved < -threshold) go(index + 1);
      else if (moved > threshold) go(index - 1);
      else go(index);

      delta = 0;
      play();
    }

    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('lostpointercapture', endDrag);

    /* Touch needs one more guard than CSS can give. Once the drag is
       running, the page must be told not to scroll, or the browser takes
       the gesture back and the swipe dies a few pixels in — the finger
       keeps moving but the row does not. Only a non-passive touchmove can
       say that, so this listener is registered as one; it does nothing
       until a horizontal drag is actually under way, leaving ordinary
       vertical scrolling untouched. */
    el.addEventListener(
      'touchmove',
      function (event) {
        if (dragging && event.cancelable) event.preventDefault();
      },
      { passive: false }
    );

    if (prev) prev.addEventListener('click', function () { stop(); go(index - 1); play(); });
    if (next) next.addEventListener('click', function () { stop(); go(index + 1); play(); });

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', play);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', play);

    window.addEventListener('resize', measure, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(measure).observe(viewport);

    /* Which mode is in force is a media query, and a query can flip without
       a resize — a device switching to a mouse, or a desktop browser told to
       emulate a phone. Re-measure when it does, or the script would keep
       dragging a transform while the stylesheet expects native scrolling. */
    if (window.matchMedia) {
      var coarse = window.matchMedia('(hover: none), (pointer: coarse)');
      if (coarse.addEventListener) coarse.addEventListener('change', measure);
      else if (coarse.addListener) coarse.addListener(measure);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else play();
    });

    measure();
    play();
  }

  /* -------------------------------------------------------------- compare */

  /* Before/after wipe. The split is published as --mdm-compare-p, a
     percentage measured from the INLINE-start edge, so the same number works
     in both reading directions and the CSS decides which side it clips.

       <div data-mdm-compare="50">
         …two stacked layers…
         <button data-mdm-compare-handle role="slider">
       </div>
  */
  function setupCompare(el) {
    if (el.__mdmCompare) return;
    el.__mdmCompare = true;

    var handle = el.querySelector('[data-mdm-compare-handle]');
    var start = parseFloat(el.getAttribute('data-mdm-compare'));
    if (isNaN(start)) start = 50;

    var value = start;
    var dragging = false;

    function isRtl() {
      return window.getComputedStyle(el).direction === 'rtl';
    }

    /* Nothing is written until the visitor interacts, which leaves the
       opening wipe to CSS — an inline value would override it instantly. */
    function commit() {
      el.style.setProperty('--mdm-compare-p', value.toFixed(2) + '%');
      if (handle) handle.setAttribute('aria-valuenow', Math.round(value));
    }

    function fromClientX(x) {
      var rect = el.getBoundingClientRect();
      if (!rect.width) return value;
      var ratio = isRtl() ? (rect.right - x) / rect.width : (x - rect.left) / rect.width;
      return Math.max(0, Math.min(100, ratio * 100));
    }

    function engage() {
      el.classList.add('is-touched');
      el.classList.add('is-dragging');
    }

    el.addEventListener('pointerdown', function (event) {
      if (event.button) return;
      dragging = true;
      engage();
      value = fromClientX(event.clientX);
      commit();
      if (el.setPointerCapture) {
        try {
          el.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
    });

    el.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      value = fromClientX(event.clientX);
      commit();
    });

    function release() {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
    }

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);

    if (handle) {
      handle.addEventListener('keydown', function (event) {
        var step = event.shiftKey ? 10 : 2;
        // Right arrow always moves the divider right on screen, which means
        // decreasing the inline-start percentage in Arabic.
        var sign = isRtl() ? -1 : 1;
        var next = null;

        if (event.key === 'ArrowRight') next = value + step * sign;
        else if (event.key === 'ArrowLeft') next = value - step * sign;
        else if (event.key === 'Home') next = isRtl() ? 100 : 0;
        else if (event.key === 'End') next = isRtl() ? 0 : 100;

        if (next === null) return;
        event.preventDefault();
        engage();
        value = Math.max(0, Math.min(100, next));
        commit();
      });

      handle.addEventListener('blur', function () {
        el.classList.remove('is-dragging');
      });
    }
  }

  /* ----------------------------------------------------------------- rail */

  /* A native scroll-snap rail. The browser does the scrolling, so touch
     momentum and snapping feel exactly like the platform — a JS transform
     slider cannot match that. All this adds is a progress indicator.

       <div id="X" data-mdm-rail> …cards… </div>
       <div data-mdm-rail-progress="X">
         <span data-mdm-rail-track><span data-mdm-rail-thumb></span></span>
       </div>
  */
  function setupRail(rail) {
    if (rail.__mdmRail) return;
    rail.__mdmRail = true;

    var ui = rail.id
      ? document.querySelector('[data-mdm-rail-progress="' + rail.id + '"]')
      : null;
    var track = ui && ui.querySelector('[data-mdm-rail-track]');
    var thumb = ui && ui.querySelector('[data-mdm-rail-thumb]');
    var nudged = false;

    /* Geometry only changes on resize, so it is cached. That leaves the
       scroll handler reading nothing but scrollLeft, which is cheap enough
       to run inline — no rAF latch that could stick if a frame never
       arrives (a hidden tab will do exactly that). */
    var max = 0;
    var trackWidth = 0;
    var thumbWidth = 0;
    var scrollable = false;

    function position() {
      if (!scrollable || !thumb) return;

      /* RTL reports scrollLeft as negative in current browsers, so the
         absolute value works for both directions. */
      var progress = Math.min(1, Math.abs(rail.scrollLeft) / max);
      thumb.style.setProperty(
        '--mdm-rail-x',
        (progress * (trackWidth - thumbWidth)).toFixed(1) + 'px'
      );

      if (!nudged && progress > 0.015) {
        nudged = true;
        if (ui) ui.classList.add('is-scrolled');
      }
    }

    function measure(attempt) {
      max = rail.scrollWidth - rail.clientWidth;
      scrollable = max > 4;

      if (ui) ui.classList.toggle('is-scrollable', scrollable);
      if (!scrollable || !track || !thumb) return;

      trackWidth = track.getBoundingClientRect().width;

      /* The cue is display:none until the class above is applied, so the
         first measurement can legitimately come back as zero. Retry rather
         than give up for good, which would leave a dead indicator.
         setTimeout, not rAF: frames do not arrive in a hidden tab. */
      if (!trackWidth) {
        var tries = attempt || 0;
        if (tries < 20) {
          window.setTimeout(function () {
            measure(tries + 1);
          }, 100);
        }
        return;
      }

      thumbWidth = Math.max(24, trackWidth * (rail.clientWidth / rail.scrollWidth));
      thumb.style.setProperty('--mdm-rail-thumb', thumbWidth.toFixed(1) + 'px');
      position();
    }

    function remeasure() {
      measure();
    }

    rail.addEventListener('scroll', position, { passive: true });
    window.addEventListener('resize', remeasure, { passive: true });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(remeasure);
      ro.observe(rail);
      // Also the cue: it has no box until it becomes visible.
      if (track) ro.observe(track);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);

    measure();
  }

  /* --------------------------------------------------------------- slider */

  /* One slide at a time, auto-advancing, draggable by touch or pointer.
     Positions are computed in pixels rather than percentages so the drag can
     follow the finger exactly. In RTL the slides lay out right-to-left, so
     advancing moves the track the other way — handled by dirSign(). */
  function setupSlider(el) {
    if (el.__mdmSlider) return;
    el.__mdmSlider = true;

    var track = el.querySelector('[data-mdm-slider-track]');
    var slides = toArray(el.querySelectorAll('[data-mdm-slider-slide]'));
    if (!track || slides.length < 2) return;

    var interval = parseFloat(el.getAttribute('data-mdm-slider'));
    if (isNaN(interval) || interval < 1200) interval = 4500;

    var until = parseFloat(el.getAttribute('data-mdm-slider-until'));
    var mq = isNaN(until) ? null : window.matchMedia('(max-width: ' + until + 'px)');

    var index = 0;
    var width = 0;
    var timer = 0;
    var active = false;
    var dragging = false;
    var startX = 0;
    var delta = 0;

    function dirSign() {
      return window.getComputedStyle(el).direction === 'rtl' ? -1 : 1;
    }

    function baseX() {
      return -index * width * dirSign();
    }

    function place(x, animate) {
      track.style.transition = animate ? '' : 'none';
      track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    /* The slide's own width, not the container's: the container carries the
       horizontal padding, the track does not, so measuring the container
       would drift the slides by the padding on every step. */
    function measureSlide() {
      return slides[0].getBoundingClientRect().width;
    }

    function go(next, animate) {
      width = measureSlide();
      index = ((next % slides.length) + slides.length) % slides.length;
      place(baseX(), animate !== false);
      slides.forEach(function (slide, i) {
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
    }

    function stop() {
      window.clearInterval(timer);
      timer = 0;
    }

    function play() {
      stop();
      if (!active || reduced() || document.hidden) return;
      timer = window.setInterval(function () {
        go(index + 1);
      }, interval);
    }

    function enable() {
      if (active) return;
      active = true;
      el.classList.add('is-slider');
      go(0, false);
      play();
    }

    function disable() {
      if (!active && !el.classList.contains('is-slider')) return;
      active = false;
      stop();
      el.classList.remove('is-slider');
      track.style.transition = '';
      track.style.transform = '';
      slides.forEach(function (slide) {
        slide.removeAttribute('aria-hidden');
      });
    }

    function sync() {
      if (!mq || mq.matches) enable();
      else disable();
    }

    /* ---- drag ---- */
    el.addEventListener('pointerdown', function (event) {
      if (!active || event.button) return;
      dragging = true;
      startX = event.clientX;
      delta = 0;
      stop();
      width = measureSlide();
      if (el.setPointerCapture) {
        try {
          el.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
    });

    el.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      delta = event.clientX - startX;
      place(baseX() + delta, false);
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;

      var threshold = Math.max(28, width * 0.18);
      // delta * dirSign is negative when the gesture means "next" in either
      // reading direction.
      var moved = delta * dirSign();

      if (moved < -threshold) go(index + 1);
      else if (moved > threshold) go(index - 1);
      else go(index);

      delta = 0;
      play();
    }

    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('pointerleave', endDrag);

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', play);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', play);

    window.addEventListener(
      'resize',
      function () {
        sync();
        if (active) go(index, false);
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else play();
    });

    if (mq && mq.addEventListener) mq.addEventListener('change', sync);
    sync();
  }

  /* -------------------------------------------------------------- gallery */

  /* Product gallery: one large stage that crossfades, plus a thumbnail strip
     that scrolls natively and keeps the active thumb in view. Built for long
     image sets — arrows, a counter, swipe and keyboard all address the same
     index, so nothing can drift out of sync.

       <div data-mdm-gallery="loop">
         <div data-mdm-gallery-stage>
           <div data-mdm-gallery-slide> <img> </div>
         </div>
         <button data-mdm-gallery-prev></button>
         <button data-mdm-gallery-next></button>
         <span data-mdm-gallery-count></span>
         <div data-mdm-gallery-thumbs>
           <button data-mdm-gallery-thumb></button>
         </div>
       </div>
  */
  function setupGallery(el) {
    if (el.__mdmGallery) return;
    el.__mdmGallery = true;

    var slides = toArray(el.querySelectorAll('[data-mdm-gallery-slide]'));
    if (!slides.length) return;

    var thumbs = toArray(el.querySelectorAll('[data-mdm-gallery-thumb]'));
    var strip = el.querySelector('[data-mdm-gallery-thumbs]');
    var stage = el.querySelector('[data-mdm-gallery-stage]');
    var prev = el.querySelector('[data-mdm-gallery-prev]');
    var next = el.querySelector('[data-mdm-gallery-next]');
    var counter = el.querySelector('[data-mdm-gallery-count]');
    var loop = el.getAttribute('data-mdm-gallery') === 'loop';
    var last = slides.length - 1;
    var index = 0;
    var onChange = null;
    var swiped = false;

    function isRtl() {
      return window.getComputedStyle(el).direction === 'rtl';
    }

    /* Nudges the strip only when the thumb is actually out of view, so
       clicking a visible thumb never jumps the strip under the cursor. */
    function revealThumb(thumb) {
      if (!strip || !thumb) return;
      var sb = strip.getBoundingClientRect();
      var tb = thumb.getBoundingClientRect();
      if (tb.left >= sb.left - 1 && tb.right <= sb.right + 1) return;
      var delta = tb.left + tb.width / 2 - (sb.left + sb.width / 2);

      /* Smooth scrolling is an animation, and animations do not tick in a
         hidden tab — the strip would silently never move, leaving the active
         thumb off screen for the rest of the session. Jump instantly
         whenever the smooth path cannot be trusted. */
      if (reduced() || document.hidden || !strip.scrollBy) {
        strip.scrollLeft += delta;
        return;
      }

      strip.scrollBy({ left: delta, behavior: 'smooth' });
    }

    function go(to, fromThumb) {
      if (to < 0) to = loop ? last : 0;
      if (to > last) to = loop ? 0 : last;
      index = to;

      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });

      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
        thumb.tabIndex = i === index ? 0 : -1;
      });

      if (counter) counter.textContent = index + 1 + ' / ' + slides.length;

      if (!loop) {
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === last;
      }

      if (!fromThumb) revealThumb(thumbs[index]);
      if (onChange) onChange();
    }

    /* Arrows are labelled by intent, not by screen side: "next" always means
       the following image, and the glyph is flipped in CSS for RTL. */
    if (prev) {
      prev.addEventListener('click', function () {
        go(index - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(index + 1);
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        go(i, true);
      });
    });

    if (strip) {
      strip.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowRight') step = isRtl() ? -1 : 1;
        else if (event.key === 'ArrowLeft') step = isRtl() ? 1 : -1;
        else if (event.key === 'Home') step = -index;
        else if (event.key === 'End') step = last - index;
        else return;

        event.preventDefault();
        go(index + step);
        if (thumbs[index]) thumbs[index].focus();
      });
    }

    /* Swipe. Pointer events cover mouse, pen and touch in one path; the
       vertical guard lets a downward flick scroll the page instead. */
    if (stage && window.PointerEvent) {
      var startX = 0;
      var startY = 0;
      var tracking = false;

      stage.addEventListener(
        'pointerdown',
        function (event) {
          if (event.button && event.button !== 0) return;
          tracking = true;
          startX = event.clientX;
          startY = event.clientY;
        },
        { passive: true }
      );

      stage.addEventListener(
        'pointerup',
        function (event) {
          if (!tracking) return;
          tracking = false;
          var dx = event.clientX - startX;
          var dy = event.clientY - startY;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          // A swipe is not a tap: the click that follows must not open the
          // full-screen view.
          swiped = true;
          window.setTimeout(function () {
            swiped = false;
          }, 0);
          // Dragging left reveals the next image, mirrored in RTL.
          go(index + (dx < 0 ? 1 : -1) * (isRtl() ? -1 : 1));
        },
        { passive: true }
      );

      stage.addEventListener('pointercancel', function () {
        tracking = false;
      });
    }

    /* ---- Full-screen view ----
       The overlay lives outside the gallery, as a direct child of the
       section: the gallery column is sticky and animated, and a transformed
       ancestor would pin a fixed overlay to that column instead of the
       screen. Linked by id.

         <div data-mdm-gallery data-mdm-gallery-lightbox="<id>">
           <button data-mdm-gallery-expand></button>
         </div>
         <div id="<id>" hidden>
           <div data-mdm-lightbox-slide> <img> </div>
           <button data-mdm-lightbox-prev|next|close></button>
           <span data-mdm-lightbox-count></span>
         </div>

       It shares the gallery's index, so closing lands on the image last
       viewed full screen. */
    var box = document.getElementById(el.getAttribute('data-mdm-gallery-lightbox') || '');

    if (box && stage && !box.__mdmLightbox) {
      box.__mdmLightbox = true;

      var bSlides = toArray(box.querySelectorAll('[data-mdm-lightbox-slide]'));
      var bCount = box.querySelector('[data-mdm-lightbox-count]');
      var bPrev = box.querySelector('[data-mdm-lightbox-prev]');
      var bNext = box.querySelector('[data-mdm-lightbox-next]');
      var bClose = box.querySelector('[data-mdm-lightbox-close]');
      var expand = el.querySelector('[data-mdm-gallery-expand]');
      var lastFocus = null;
      var hideTimer = 0;

      var sync = function () {
        bSlides.forEach(function (slide, i) {
          var on = i === index;
          slide.classList.toggle('is-active', on);
          slide.setAttribute('aria-hidden', on ? 'false' : 'true');
          if (on) {
            // Start fetching the full-size file the moment it is needed.
            var img = slide.querySelector('img');
            if (img && img.loading === 'lazy') img.loading = 'eager';
          }
        });
        if (bCount) bCount.textContent = index + 1 + ' / ' + slides.length;
        if (!loop) {
          if (bPrev) bPrev.disabled = index === 0;
          if (bNext) bNext.disabled = index === last;
        }
      };

      var isOpen = function () {
        return !box.hidden;
      };

      var openBox = function () {
        if (isOpen()) return;
        lastFocus = document.activeElement;
        window.clearTimeout(hideTimer);
        box.hidden = false;
        sync();
        docEl.classList.add('mdm-lightbox-open');
        /* The hidden → shown change has to be committed before the fade-in
           class, or the transition has nothing to start from. A forced
           layout read commits it synchronously — waiting for animation
           frames instead would leave the overlay invisible in any tab that
           is not drawing frames. */
        void box.offsetWidth;
        box.classList.add('is-open');
        if (bClose) bClose.focus({ preventScroll: true });
      };

      var closeBox = function () {
        if (!isOpen()) return;
        box.classList.remove('is-open');
        docEl.classList.remove('mdm-lightbox-open');
        hideTimer = window.setTimeout(function () {
          box.hidden = true;
        }, reduced() || document.hidden ? 0 : 380);
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      };

      onChange = function () {
        if (isOpen()) sync();
      };

      // Tapping the photo opens it; the arrows, counter and expand button keep
      // their own jobs.
      stage.addEventListener('click', function (event) {
        if (swiped) return;
        if (event.target.closest('button, a')) return;
        if (!event.target.closest('[data-mdm-gallery-slide]')) return;
        openBox();
      });

      if (expand) expand.addEventListener('click', openBox);
      if (bClose) bClose.addEventListener('click', closeBox);
      if (bPrev) bPrev.addEventListener('click', function () { go(index - 1); });
      if (bNext) bNext.addEventListener('click', function () { go(index + 1); });

      // A tap on the dark surround (not the photo, not a control) closes.
      box.addEventListener('click', function (event) {
        if (event.target.closest('img, button')) return;
        closeBox();
      });

      box.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeBox();
          return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          var forward = event.key === 'ArrowRight' ? 1 : -1;
          go(index + forward * (isRtl() ? -1 : 1));
          return;
        }
        if (event.key !== 'Tab') return;
        // Keep keyboard focus inside the overlay.
        var items = [bClose, bPrev, bNext].filter(function (b) {
          return b && !b.disabled;
        });
        if (!items.length) return;
        var first = items[0];
        var lastItem = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          lastItem.focus();
        } else if (!event.shiftKey && document.activeElement === lastItem) {
          event.preventDefault();
          first.focus();
        }
      });

      // Swipe between photos full screen, same rule as the gallery.
      if (window.PointerEvent) {
        var bx = 0;
        var by = 0;
        var bTracking = false;
        box.addEventListener('pointerdown', function (event) {
          if (event.button && event.button !== 0) return;
          bTracking = true;
          bx = event.clientX;
          by = event.clientY;
        }, { passive: true });
        box.addEventListener('pointerup', function (event) {
          if (!bTracking) return;
          bTracking = false;
          var dx = event.clientX - bx;
          var dy = event.clientY - by;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          go(index + (dx < 0 ? 1 : -1) * (isRtl() ? -1 : 1));
        }, { passive: true });
        box.addEventListener('pointercancel', function () {
          bTracking = false;
        });
      }

      MdmMotionDebug.lightbox = function () {
        return { open: isOpen(), index: index, shown: bSlides.map(function (s) { return s.classList.contains('is-active'); }) };
      };
    }

    go(0);
  }

  /* ------------------------------------------------------------ quantity */

  /* Ties a stepper and a set of preset options to one quantity input. The
     presets are plain radios carrying the quantity they stand for, so a
     bundle app that keys off line-item quantity sees exactly what the
     shopper picked — nothing here touches price or discounts.

       <div data-mdm-qty>
         <input type="radio" data-mdm-qty-option value="3">
         <button data-mdm-qty-step="-1"></button>
         <input data-mdm-qty-input value="1">
         <button data-mdm-qty-step="1"></button>
       </div>
  */
  function setupQtyGroup(el) {
    if (el.__mdmQty) return;
    el.__mdmQty = true;

    var input = el.querySelector('[data-mdm-qty-input]');
    if (!input) return;

    var options = toArray(el.querySelectorAll('[data-mdm-qty-option]'));
    var steps = toArray(el.querySelectorAll('[data-mdm-qty-step]'));

    /* Readouts can live anywhere on the page — a sticky bar is outside the
       form — so they are addressed by the group's name, the same way the
       rail addresses its progress indicator. */
    var groupId = el.getAttribute('data-mdm-qty');
    var readouts = groupId
      ? toArray(document.querySelectorAll('[data-mdm-qty-total="' + groupId + '"]'))
      : [];

    /* Only a preset knows what its bundle costs. A hand-typed quantity has
       no price we can trust, so the readout falls back to the unit price
       rather than showing a stale bundle total. */
    function paintReadouts(option) {
      readouts.forEach(function (node) {
        var price = option && option.getAttribute('data-mdm-qty-price');
        var base = node.getAttribute('data-mdm-qty-base');
        var next = price || base;
        if (next && node.textContent.trim() !== next) node.textContent = next;
      });
    }

    function clamp(value) {
      /* Parsed, not coerced: the cart uses min="0" to mean "remove", and
         `|| 1` would quietly turn that into a minimum of one. */
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;
      var n = parseInt(value, 10);
      if (!n || n < min) n = min;
      var max = parseInt(input.getAttribute('max'), 10);
      if (max && n > max) n = max;
      return n;
    }

    function set(value, fromOption) {
      var n = clamp(value);
      if (String(n) !== input.value) {
        input.value = n;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Typing a quantity that happens to match a preset selects it; any
         other number leaves every preset clear, which is the honest state. */
      if (!fromOption) {
        options.forEach(function (option) {
          /* A preset the shop cannot supply is never selected, even when the
             typed number happens to match it. */
          option.checked = !option.disabled && parseInt(option.value, 10) === n;
        });
      }

      paintReadouts(checkedOption());
      syncSteps(n);
    }

    /* Spends the stepper buttons at the floor and the ceiling, so the
       control shows the limit rather than silently ignoring the press. */
    function syncSteps(n) {
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;
      var max = parseInt(input.getAttribute('max'), 10);
      steps.forEach(function (button) {
        var dir = parseInt(button.getAttribute('data-mdm-qty-step'), 10) || 0;
        var spent = (dir < 0 && n <= min) || (dir > 0 && !!max && n >= max);
        button.disabled = spent;
        button.setAttribute('aria-disabled', spent ? 'true' : 'false');
      });
    }

    function checkedOption() {
      for (var i = 0; i < options.length; i++) {
        if (options[i].checked) return options[i];
      }
      return null;
    }

    options.forEach(function (option) {
      option.addEventListener('change', function () {
        if (option.disabled) {
          /* Checking a disabled radio clears the rest of the group, so the
             selection is re-derived from the quantity rather than left with
             nothing marked. */
          option.checked = false;
          set(clamp(input.value));
          return;
        }
        if (option.checked) set(option.value, true);
      });
      if (option.checked && !option.disabled) set(option.value, true);
    });

    steps.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        if (button.disabled) return;
        set(clamp(input.value) + (parseInt(button.getAttribute('data-mdm-qty-step'), 10) || 0));
      });
    });

    input.addEventListener('change', function () {
      var n = clamp(input.value);
      if (String(n) !== input.value) input.value = n;
      options.forEach(function (option) {
        option.checked = !option.disabled && parseInt(option.value, 10) === n;
      });
      paintReadouts(checkedOption());
      syncSteps(n);
    });

    paintReadouts(checkedOption());
    syncSteps(clamp(input.value));
  }

  /* ----------------------------------------------------------------- tabs */

  /* A tablist and a stack of panels that crossfade. Every control addresses
     one index, so the buttons, the panels and the keyboard can never drift
     apart.

       <div data-mdm-tabs>
         <div role="tablist">
           <button data-mdm-tab aria-controls="p1"> … </button>
         </div>
         <div data-mdm-tabpanel id="p1"> … </div>
       </div>
  */
  function setupTabs(el) {
    if (el.__mdmTabs) return;
    el.__mdmTabs = true;

    var tabs = toArray(el.querySelectorAll('[data-mdm-tab]'));
    var panels = toArray(el.querySelectorAll('[data-mdm-tabpanel]'));
    if (!tabs.length) return;

    var last = tabs.length - 1;
    var index = 0;

    function isRtl() {
      return window.getComputedStyle(el).direction === 'rtl';
    }

    function select(to, focus) {
      if (to < 0) to = last;
      if (to > last) to = 0;
      index = to;

      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        /* Roving tabindex: the tablist is one stop, and the arrow keys move
           within it. */
        tab.tabIndex = on ? 0 : -1;
      });

      panels.forEach(function (panel, i) {
        var on = i === index;
        panel.classList.toggle('is-active', on);
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      if (focus && tabs[index]) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(i);
      });
    });

    el.addEventListener('keydown', function (event) {
      if (!event.target.closest('[data-mdm-tab]')) return;

      var step = 0;
      switch (event.key) {
        // The list reads top to bottom, so the vertical keys are the plain
        // ones; the horizontal pair is mirrored for Arabic.
        case 'ArrowDown':
          step = 1;
          break;
        case 'ArrowUp':
          step = -1;
          break;
        case 'ArrowRight':
          step = isRtl() ? -1 : 1;
          break;
        case 'ArrowLeft':
          step = isRtl() ? 1 : -1;
          break;
        case 'Home':
          step = -index;
          break;
        case 'End':
          step = last - index;
          break;
        default:
          return;
      }

      event.preventDefault();
      select(index + step, true);
    });

    select(0);
  }

  /* ------------------------------------------------------------------ spy */

  /* Marks the contents link for whatever the reader has reached. The
     observer only reports crossings, so the callback re-reads every target
     and picks the last one to have passed the reading line — that stays
     correct however fast the page was scrolled or jumped.

       <nav data-mdm-spy>
         <a href="#clause-1" data-mdm-spy-link="clause-1"> … </a>
       </nav>
  */
  function setupSpy(el) {
    if (el.__mdmSpy) return;
    el.__mdmSpy = true;

    var links = toArray(el.querySelectorAll('[data-mdm-spy-link]'));
    if (!links.length || !window.IntersectionObserver) return;

    var targets = [];
    links.forEach(function (link) {
      var target = document.getElementById(link.getAttribute('data-mdm-spy-link'));
      if (target) targets.push(target);
    });
    if (!targets.length) return;

    var current = null;

    function mark(id) {
      if (id === current) return;
      current = id;
      links.forEach(function (link) {
        var on = link.getAttribute('data-mdm-spy-link') === id;
        link.classList.toggle('is-current', on);
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    function pick() {
      /* The last clause can never reach the reading line — there is not
         enough page left below it to scroll — so the bottom of the document
         counts as having reached it. Without this the final entry could
         never be marked. */
      if (window.innerHeight + window.pageYOffset >= docEl.scrollHeight - 4) {
        mark(targets[targets.length - 1].id);
        return;
      }

      var line = window.innerHeight * 0.3;
      var best = null;
      var bestTop = -Infinity;

      for (var i = 0; i < targets.length; i++) {
        var top = targets[i].getBoundingClientRect().top;
        // The last heading to have crossed the reading line is the one being
        // read; above it they have all crossed, below none have.
        if (top <= line && top > bestTop) {
          bestTop = top;
          best = targets[i];
        }
      }

      mark((best || targets[0]).id);
    }

    /* Held on the element: an observer whose only reference is its own
       observation can be collected, and the marking would stop for good. */
    el.__mdmSpyIO = new IntersectionObserver(pick, {
      rootMargin: '-30% 0px -60% 0px',
      threshold: 0
    });

    targets.forEach(function (target) {
      el.__mdmSpyIO.observe(target);
    });

    /* The observer only reports crossings, and the bottom of the page is not
       one. Reading eight rects is cheap enough to do straight off scroll —
       and a rAF latch here could stick if a frame never arrived. */
    window.addEventListener('scroll', pick, { passive: true });

    pick();
  }

  /* ---------------------------------------------------------- autosubmit */

  /* A form that saves itself when a watched control changes, so a cart
     quantity does not need an Update button pressed after it. Debounced: a
     stepper can fire several changes in a row, and each one would otherwise
     be its own page load.

       <form data-mdm-autosubmit="550">
         <input data-mdm-autosubmit-watch>
       </form>

     Without the script the form still has its own submit button, so the
     cart is never dependent on this running. */
  function setupAutoSubmit(el) {
    if (el.__mdmAutoSubmit) return;
    el.__mdmAutoSubmit = true;

    /* The marker may sit on the form or on anything inside it, so the form
       is resolved rather than assumed. */
    var form = el.tagName === 'FORM' ? el : el.closest('form');
    if (!form) return;

    var delay = parseFloat(el.getAttribute('data-mdm-autosubmit'));
    if (isNaN(delay) || delay < 0) delay = 550;

    var timer = 0;
    var sent = false;

    el.addEventListener('change', function (event) {
      if (sent) return;
      if (!event.target.closest('[data-mdm-autosubmit-watch]')) return;

      // Marks the form as saving so the section can dim while it reloads.
      form.classList.add('is-busy');

      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        /* requestSubmit runs constraint validation and simply does nothing
           when the form is invalid, which would leave it dimmed forever.
           Hand the problem back to the shopper instead. */
        if (form.checkValidity && !form.checkValidity()) {
          form.classList.remove('is-busy');
          if (form.reportValidity) form.reportValidity();
          return;
        }

        sent = true;
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      }, delay);
    });
  }

  /* ---------------------------------------------------------------- float */

  /* A floating action button. Either shows straight away, or waits until the
     page has been scrolled far enough that it is not covering the top of the
     page before the shopper has read anything.

       <a data-mdm-float="0">   …  visible immediately
       <a data-mdm-float="300"> …  visible past 300px
  */
  function setupFloat(el) {
    if (el.__mdmFloat) return;
    el.__mdmFloat = true;

    var after = parseFloat(el.getAttribute('data-mdm-float'));

    if (isNaN(after) || after <= 0) {
      el.classList.add('is-visible');
      return;
    }

    /* Runs straight off the scroll event. Reading pageYOffset and toggling
       one class is cheap, and a rAF latch can stick permanently if a frame
       never arrives — which is exactly what a background tab does. */
    function apply() {
      el.classList.toggle('is-visible', window.pageYOffset >= after);
    }

    window.addEventListener('scroll', apply, { passive: true });
    apply();
  }

  /* ----------------------------------------------------------- sticky bar */

  /* A fixed bar that slides in once its anchor has scrolled off the top of
     the viewport, and slides away when the anchor comes back. Used for a
     mobile buy bar, so it never covers the real button.

       <div data-mdm-sticky-bar="<id of the anchor element>"> … </div>
  */
  function setupStickyBar(el) {
    if (el.__mdmStickyBar) return;
    el.__mdmStickyBar = true;

    var anchor = document.getElementById(el.getAttribute('data-mdm-sticky-bar') || '');

    /* Published so a section can reserve room for the bar rather than let it
       cover the last thing on the page. Zero while hidden, and zero on
       desktop where the bar is display:none and has no height. */
    function publishHeight() {
      var visible = el.classList.contains('is-visible');
      docEl.style.setProperty('--mdm-sticky-bar-h', (visible ? el.offsetHeight : 0) + 'px');
    }

    var canInert = 'inert' in el;

    function show(on) {
      if (el.classList.contains('is-visible') === on) return;
      el.classList.toggle('is-visible', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      /* CSS flips visibility only once the slide has finished, which leaves
         the button tabbable for the length of the transition. inert closes
         that gap immediately. */
      if (canInert) el.inert = !on;
      publishHeight();
    }

    // The bar starts hidden, and show() short-circuits when the state
    // already matches, so the starting state is set here rather than there.
    if (canInert) el.inert = true;

    if (!anchor || !window.IntersectionObserver) {
      // No anchor to key off: better a bar that is always there than none.
      show(true);
      return;
    }

    /* Held on the element, not left anonymous: an observer whose only
       reference is its own observation can be collected, and then the bar
       never appears again. Every other observer in this file is retained
       the same way. */
    el.__mdmStickyBarIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Above the viewport only. Approaching the button from below —
          // scrolling up the page — must not raise the bar.
          show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );

    el.__mdmStickyBarIO.observe(anchor);

    if (window.ResizeObserver) new ResizeObserver(publishHeight).observe(el);
  }

  /* -------------------------------------------------------- sticky header */

  /* The header lies transparently over the section below it while the page is
     at the top, then pins to the top of the window and takes a background.

     The swap from absolute to fixed happens at exactly the scroll position
     where the header reaches the top of the window, so it occupies the same
     place either side of the switch — no jump, and nothing to animate. */
  function setupStickyHeader(el) {
    if (el.__mdmHeader) return;
    el.__mdmHeader = true;

    var transparent = el.getAttribute('data-mdm-header-transparent') === 'true';
    var wrap = el.parentElement;
    var pinAt = 0;

    /* Two heights are published.
         --mdm-header-h        the live height, compact or not — for anything
                               sticky that sits below the header
         --mdm-header-rest-h   the resting (full) height — what the page
         --mdm-header-overlay-h  reserves for the header. The compact bar, and
                               every frame of the transition to and from it,
                               must never change these, or the page would
                               move under the reader. */
    function publishLive() {
      docEl.style.setProperty('--mdm-header-h', el.offsetHeight + 'px');
    }

    function publishRest(h) {
      // Overlay room only matters while the header lies over the section below.
      docEl.style.setProperty('--mdm-header-overlay-h', (transparent ? h : 0) + 'px');
      docEl.style.setProperty('--mdm-header-rest-h', h + 'px');
    }

    /* The resting height measured directly, whatever state the bar is in:
       rest classes and no transitions for a single synchronous read, then put
       back. Nothing paints in between. It would cut a running size transition
       short, so it is only used on setup, resize and load — never per frame. */
    function restHeight() {
      var compact = el.classList.contains('is-scrolled');
      el.classList.add('mdm-instant');
      if (compact) el.classList.remove('is-scrolled');
      var h = el.offsetHeight;
      if (compact) el.classList.add('is-scrolled');
      void el.offsetWidth;
      el.classList.remove('mdm-instant');
      return h;
    }

    /* True while the bar or logo is still easing between sizes. A timer
       cannot stand in for this: a slow device or a background tab can leave
       the transition mid-way long after any fixed delay. */
    function sizeAnimating() {
      if (!el.getAnimations) return false;
      return el.getAnimations({ subtree: true }).some(function (animation) {
        return /padding|width|height|font-size/.test(animation.transitionProperty || '');
      });
    }

    // Size observer and transition ends: the live height always, the resting
    // height only once the bar has come fully to rest.
    function publishHeight() {
      publishLive();
      if (!el.classList.contains('is-scrolled') && !sizeAnimating()) publishRest(el.offsetHeight);
    }

    function publishAll() {
      publishLive();
      publishRest(restHeight());
    }

    // Compact state: shorter bar, smaller logo.
    function setCompact(on) {
      if (el.classList.contains('is-scrolled') === on) return;
      el.classList.toggle('is-scrolled', on);
    }

    el.addEventListener('transitionend', publishHeight);

    /* Where the header naturally sits in the document: directly below the
       announcement bar, or 0 when there isn't one. The wrapper stays in flow
       at that spot whatever the header itself is doing, so this is stable. */
    function measure() {
      publishAll();
      if (!transparent || !wrap) {
        pinAt = 0;
        return;
      }
      pinAt = Math.max(0, wrap.getBoundingClientRect().top + window.pageYOffset);
    }

    /* Runs straight off the scroll event. Reading pageYOffset and toggling
       two classes is cheap, and a rAF latch can stick permanently if a frame
       never arrives — which is exactly what a background tab does. */
    function apply() {
      var y = window.pageYOffset;
      el.classList.toggle('is-fixed', y >= pinAt);
      // Transparent only while the page is genuinely at the top.
      el.classList.toggle('is-solid', y > 6);
      // The compact state (shorter bar, smaller logo) — the same moment in
      // both variants.
      setCompact(y > 6);
    }

    /* A page opened part-way down starts compact rather than animating into
       it on load. The resting height is published first, while the bar is
       still at rest. */
    if (!transparent) {
      publishAll();
      withoutTransition(el, function () {
        setCompact(window.pageYOffset > 6);
      });
      window.addEventListener(
        'scroll',
        function () {
          setCompact(window.pageYOffset > 6);
        },
        { passive: true }
      );
      window.addEventListener('resize', publishAll, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(publishHeight).observe(el);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(publishAll);
      if (document.readyState === 'complete') window.setTimeout(publishAll, 0);
      else window.addEventListener('load', publishAll);
      return;
    }

    measure();
    withoutTransition(el, apply);

    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        measure();
        apply();
      },
      { passive: true }
    );

    if (window.ResizeObserver) new ResizeObserver(publishHeight).observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    if (document.readyState === 'complete') window.setTimeout(measure, 0);
    else window.addEventListener('load', measure);

    MdmMotionDebug.header = function () {
      return {
        transparent: transparent,
        pinAt: Math.round(pinAt),
        scrollY: Math.round(window.pageYOffset),
        pinned: el.classList.contains('is-fixed'),
        solid: el.classList.contains('is-solid'),
        headerHeight: el.offsetHeight
      };
    };
  }

  /* --------------------------------------------------------------- drawer */

  /* Off-canvas panel. Opened by any [data-mdm-drawer-toggle="<drawer id>"]. */
  function setupDrawer(el) {
    if (el.__mdmDrawer) return;
    el.__mdmDrawer = true;

    var lastFocus = null;
    var closeTimer = 0;

    function toggles() {
      return toArray(document.querySelectorAll('[data-mdm-drawer-toggle="' + el.id + '"]'));
    }

    function setExpanded(value) {
      toggles().forEach(function (t) {
        t.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    function open() {
      if (!el.hidden) return;
      window.clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      el.hidden = false;
      docEl.classList.add('mdm-drawer-open');
      setExpanded(true);

      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          el.classList.add('is-open');
        });
      });

      window.setTimeout(function () {
        var first = el.querySelector('[data-mdm-drawer-close], a[href], button');
        if (first) first.focus();
      }, 80);
    }

    function close() {
      if (el.hidden) return;
      el.classList.remove('is-open');
      docEl.classList.remove('mdm-drawer-open');
      setExpanded(false);
      closeTimer = window.setTimeout(function () {
        el.hidden = true;
      }, reduced() ? 0 : 460);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    el.addEventListener('click', function (event) {
      if (event.target.closest('[data-mdm-drawer-close]')) close();
    });

    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-mdm-drawer-toggle="' + el.id + '"]');
      if (!toggle) return;
      event.preventDefault();
      if (el.hidden) open();
      else close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !el.hidden) close();
    });

    el.__mdmDrawerClose = close;
  }

  /* ------------------------------------------------------------- judge.me */

  /* A "Write a review" button of our own that opens Judge.me's own
     write-a-review popup.

       <a href="/products/x#judgeme" data-mdm-jdgm-open="<host id>">…</a>
       <div id="<host id>" data-mdm-jdgm-host> Judge.me review widget </div>

     The host holds Judge.me's standard review widget, laid out but out of
     sight. Judge.me builds its popup from that widget and adds it to <body>,
     so the popup itself is never inside anything hidden. Opening it means
     clicking the widget's own write-review link, which keeps Judge.me in
     charge of the whole flow.

     Judge.me's script shapes the rest:
       · Its first click on a write link only loads the form scripts and is
         swallowed, so the link is clicked again until the popup shows —
         and never once it has, or the click could land mid-fade.
       · The write link is href="#". Its default is cancelled so the page
         does not jump to the top while the scripts are still loading.
     If Judge.me never loads, or is set to show its form inline instead of
     in a popup, the button's own link is followed: the product page with
     #judgeme, which Judge.me opens its form on. */
  var JDGM_LINK =
    '.jdgm-write-rev-link, [data-jdgm-write-review], ' +
    'button[class*="write-review" i], a[class*="write-review" i], ' +
    'button[class*="write-rev" i], a[class*="write-rev" i]';
  var JDGM_POPUP = '.jdgm-write-review-modal';

  function setupJudgeMe(trigger) {
    if (trigger.__mdmJdgm) return;
    trigger.__mdmJdgm = true;

    var running = 0;

    function shown(node) {
      return !!node && window.getComputedStyle(node).display !== 'none' && node.getClientRects().length > 0;
    }

    function host() {
      return document.getElementById(trigger.getAttribute('data-mdm-jdgm-open'));
    }

    function popupShown() {
      return toArray(document.querySelectorAll(JDGM_POPUP)).some(shown);
    }

    function writeLink(root) {
      var link = root.querySelector(JDGM_LINK);
      if (link) return link;

      // A redesigned widget with unfamiliar class names: match the button by
      // the label Judge.me gives it.
      var label = window.jdgmSettings && window.jdgmSettings.widget_open_form_text;
      if (!label) return null;
      label = String(label).trim().toLowerCase();
      return (
        toArray(root.querySelectorAll('a, button, [role="button"]')).filter(function (node) {
          return node.textContent.trim().toLowerCase() === label;
        })[0] || null
      );
    }

    function setBusy(busy) {
      trigger.classList.toggle('is-busy', busy);
      if (busy) trigger.setAttribute('aria-busy', 'true');
      else trigger.removeAttribute('aria-busy');
    }

    function follow() {
      var href = trigger.getAttribute('href');
      if (href && href.charAt(0) !== '#') window.location.href = href;
    }

    function preventJump(event) {
      event.preventDefault();
    }

    function request() {
      if (running || popupShown()) return;

      var root = host();
      if (!root) {
        follow();
        return;
      }

      var started = Date.now();
      var lastClick = 0;

      function stop() {
        window.clearInterval(running);
        running = 0;
        setBusy(false);
      }

      function tick() {
        if (popupShown()) {
          stop();
          return;
        }

        if (lastClick && shown(root.querySelector('.jdgm-form-wrapper'))) {
          stop();
          if (window.console) {
            console.warn(
              '[mdm] Judge.me opened its review form inline, not in a popup. ' +
                'Set Judge.me › Settings › Collection flow › "Where customers write reviews" to In-store popup.'
            );
          }
          follow();
          return;
        }

        if (Date.now() - started > 9000) {
          stop();
          follow();
          return;
        }

        var link = writeLink(root);
        if (link && Date.now() - lastClick > 900) {
          lastClick = Date.now();
          link.addEventListener('click', preventJump, { once: true });
          link.click();
        }
      }

      setBusy(true);
      running = window.setInterval(tick, 150);
      tick();
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      request();
    });

    MdmMotionDebug.jdgm = function () {
      var root = host();
      return {
        host: !!root,
        writeLink: !!(root && writeLink(root)),
        popupShown: popupShown(),
        busy: !!running,
        judgemeLoaded: !!(window.jdgm && window.jdgm.$)
      };
    };
  }

  /* ------------------------------------------------------------- discount */

  /* A discount code box on the cart.

       <div data-mdm-discount="<form id>" data-mdm-discount-applied="code,code">
         <input data-mdm-discount-input form="<form id>">
         <button type="submit" form="<form id>" data-mdm-discount-apply>
         <p data-mdm-discount-msg hidden></p>
         <button data-mdm-discount-remove="CODE">
       </div>
       <form id="<form id>" method="get" action="/checkout"></form>

     The field belongs to that separate form — forms cannot nest, and the cart
     form wraps the whole summary — which without this script simply carries
     the code to checkout, where Shopify applies it. Here the code goes onto
     the cart itself instead.

     The endpoint answers with the updated cart, so whether a code took is
     read straight from that: found means reload, so every total on the page
     comes from Shopify rather than from arithmetic done here. */
  function setupDiscount(el) {
    if (el.__mdmDiscount) return;
    el.__mdmDiscount = true;

    var form = document.getElementById(el.getAttribute('data-mdm-discount') || '');
    var input = el.querySelector('[data-mdm-discount-input]');
    var msg = el.querySelector('[data-mdm-discount-msg]');

    function applied() {
      return (el.getAttribute('data-mdm-discount-applied') || '')
        .split(',')
        .map(function (code) {
          return code.trim().toLowerCase();
        })
        .filter(Boolean);
    }

    function say(text) {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
    }

    /* Every discount title the returned cart knows about. A code can land on
       the cart as a whole or on individual lines, so both are collected. */
    function titlesIn(cart) {
      var titles = [];

      function add(list, key) {
        (list || []).forEach(function (entry) {
          var title = key ? entry[key] && entry[key].title : entry.title;
          if (title) titles.push(String(title).toLowerCase());
        });
      }

      add(cart.cart_level_discount_applications);
      add(cart.discount_applications);
      (cart.items || []).forEach(function (item) {
        add(item.discounts);
        add(item.line_level_discount_allocations, 'discount_application');
      });

      return titles;
    }

    function post(codes) {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;

      return window
        .fetch((root || '/') + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes })
        })
        .then(function (response) {
          if (!response.ok) throw new Error('the cart refused the update');
          return response.json();
        });
    }

    /* codes   what the cart should hold afterwards — one code, or nothing
       expect  the code that has to come back for this to count as applied
       restore what to put back if it does not */
    function send(codes, expect, restore) {
      el.classList.add('is-busy');
      say('');

      post(codes)
        .then(function (cart) {
          // Clearing always reloads; applying only once the code stuck.
          if (!expect || titlesIn(cart).indexOf(expect.toLowerCase()) > -1) {
            window.location.reload();
            return;
          }

          /* The code did not take, and sending it has already cleared whatever
             the cart held — so put that back before saying anything, or a
             working code would be lost to a typo. */
          var settled = restore ? post(restore).catch(function () {}) : Promise.resolve();

          return settled.then(function () {
            el.classList.remove('is-busy');
            say(el.getAttribute('data-mdm-discount-invalid') || '');
          });
        })
        .catch(function () {
          el.classList.remove('is-busy');
          say(el.getAttribute('data-mdm-discount-error') || '');
        });
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        var code = input ? input.value.trim() : '';
        if (!code) return;
        event.preventDefault();

        /* One code at a time. Sending only this one replaces whatever the cart
           held, so the code last entered is the only one that reaches
           checkout. */
        var was = applied().join(',');
        send(code, code, was || null);
      });
    }

    /* An offer elsewhere on the page — "use code NEW10" — puts its code in
       the field and applies it in one tap. The listener is on the document
       because the offer sits outside this box, above the summary. */
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-mdm-discount-fill]');
      if (!button || !input || !form) return;
      // A theme-editor re-render leaves this listener behind a detached copy.
      if (!document.contains(el)) return;

      event.preventDefault();
      input.value = button.getAttribute('data-mdm-discount-fill') || '';
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });

    el.addEventListener('click', function (event) {
      var button = event.target.closest('[data-mdm-discount-remove]');
      if (!button) return;
      event.preventDefault();

      /* Clear the cart's codes outright rather than just this one: with a
         single code allowed, removing it has to leave nothing for checkout to
         pick up. */
      send('', null, null);
    });
  }

  /* ----------------------------------------------------------------- init */

  function initRoot(root) {
    var container = root || document;
    var found = toArray(container.querySelectorAll('[data-mdm-scope]'));

    if (root && root.matches && root.matches('[data-mdm-scope]')) found.unshift(root);

    found.forEach(function (scopeEl) {
      toArray(scopeEl.querySelectorAll('[data-mdm-split]')).forEach(function (el) {
        if (splitItems.indexOf(el) === -1 && splitElement(el)) splitItems.push(el);
      });

      registerScope(scopeEl);
      registerParallax(scopeEl);
      registerMagnets(scopeEl);
      registerMarquees(scopeEl);
    });

    /* Header and drawer live outside any scope — they are chrome, not
       content, so they are never revealed on scroll. */
    toArray(container.querySelectorAll('[data-mdm-sticky-header]')).forEach(setupStickyHeader);
    toArray(container.querySelectorAll('[data-mdm-slider]')).forEach(setupSlider);
    toArray(container.querySelectorAll('[data-mdm-rail]')).forEach(setupRail);
    toArray(container.querySelectorAll('[data-mdm-compare]')).forEach(setupCompare);
    toArray(container.querySelectorAll('[data-mdm-carousel]')).forEach(setupCarousel);
    toArray(container.querySelectorAll('[data-mdm-gallery]')).forEach(setupGallery);
    toArray(container.querySelectorAll('[data-mdm-qty]')).forEach(setupQtyGroup);
    toArray(container.querySelectorAll('[data-mdm-autosubmit]')).forEach(setupAutoSubmit);
    toArray(container.querySelectorAll('[data-mdm-sticky-bar]')).forEach(setupStickyBar);
    toArray(container.querySelectorAll('[data-mdm-float]')).forEach(setupFloat);
    toArray(container.querySelectorAll('[data-mdm-accordion]')).forEach(setupAccordion);
    toArray(container.querySelectorAll('[data-mdm-tabs]')).forEach(setupTabs);
    toArray(container.querySelectorAll('[data-mdm-spy]')).forEach(setupSpy);
    toArray(container.querySelectorAll('[data-mdm-drawer]')).forEach(setupDrawer);
    toArray(container.querySelectorAll('[data-mdm-jdgm-open]')).forEach(setupJudgeMe);
    toArray(container.querySelectorAll('[data-mdm-discount]')).forEach(setupDiscount);
  }

  function destroyRoot(root) {
    if (!root) return;
    toArray(root.querySelectorAll('[data-mdm-scope]')).forEach(function (scopeEl) {
      var scope = scopeEl.__mdmScope;
      if (!scope) return;
      var index = scopes.indexOf(scope);
      if (index > -1) scopes.splice(index, 1);
      if (observer) observer.unobserve(scopeEl);
      delete scopeEl.__mdmScope;
    });

    parallaxItems = parallaxItems.filter(function (item) {
      return document.contains(item.el);
    });
    magnetItems = magnetItems.filter(function (item) {
      return document.contains(item.el);
    });
    splitItems = splitItems.filter(function (el) {
      return document.contains(el);
    });
    marqueeItems = marqueeItems.filter(function (el) {
      if (document.contains(el)) return true;
      if (el.__mdmMarqueeRO) {
        el.__mdmMarqueeRO.disconnect();
        delete el.__mdmMarqueeRO;
      }
      return false;
    });
  }

  function boot() {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var scope = entry.target.__mdmScope;
          if (!scope) return;
          // Next frame, so the initial styles are guaranteed to be committed
          // before the transition starts.
          window.requestAnimationFrame(function () {
            reveal(scope);
          });
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    initRoot(document);

    /* The loop shuts itself down once everything has settled, so scrolling has
       to wake it again. Cheap: it only looks at plates that are on screen. */
    window.addEventListener(
      'scroll',
      function () {
        for (var i = 0; i < parallaxItems.length; i++) {
          if (parallaxItems[i].visible) {
            startLoop();
            return;
          }
        }
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      function () {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(function () {
          // Ignore height-only changes: mobile browsers fire those constantly
          // as the URL bar collapses.
          if (window.innerWidth === lastWidth) return;
          lastWidth = window.innerWidth;
          relayoutSplits();
          marqueeItems.forEach(setupMarquee);
        }, 220);
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopLoop();
      else startLoop();
    });

    if (reduceQuery.addEventListener) {
      reduceQuery.addEventListener('change', function () {
        if (reduced()) {
          stopLoop();
          scopes.forEach(reveal);
        }
      });
    }

    /* Catches anything that lands in the DOM without a section:load event —
       the theme editor injecting a section, an app block, an AJAX drawer.
       initRoot is idempotent, so re-running it costs nothing. */
    if (window.MutationObserver && document.body) {
      var domTimer = 0;
      new MutationObserver(function (mutations) {
        var relevant = false;

        for (var i = 0; i < mutations.length && !relevant; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches && node.matches('[data-mdm-scope]')) ||
              (node.querySelector && node.querySelector('[data-mdm-scope]'))
            ) {
              relevant = true;
              break;
            }
          }
        }

        if (!relevant) return;
        window.clearTimeout(domTimer);
        domTimer = window.setTimeout(function () {
          initRoot(document);
        }, 60);
      }).observe(document.body, { childList: true, subtree: true });
    }

    /* Theme editor */
    document.addEventListener('shopify:section:load', function (event) {
      initRoot(event.target);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      destroyRoot(event.target);
    });
    document.addEventListener('shopify:block:select', function (event) {
      var scopeEl = event.target.closest && event.target.closest('[data-mdm-scope]');
      if (scopeEl && scopeEl.__mdmScope) reveal(scopeEl.__mdmScope);
    });
  }

  /* Line breaks have to be measured against the real face, so we wait for
     webfonts — but only briefly. A long wait is what makes a hero arrive
     after everything else on the page, so the cap is short and the font
     usually resolves from cache well inside it. */
  function whenReady() {
    var fonts =
      document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    var timeout = new Promise(function (resolve) {
      window.setTimeout(resolve, 250);
    });
    return Promise.race([fonts, timeout]);
  }

  function start() {
    whenReady().then(function () {
      try {
        boot();
      } catch (error) {
        // Never let a motion bug hide the content.
        docEl.classList.remove('mdm-js');
        if (window.console) window.console.error('[mdm-motion]', error);
      }
    });
  }

  window.MdmMotion = {
    debug: MdmMotionDebug,
    init: initRoot,
    destroy: destroyRoot,
    relayout: relayoutSplits
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
